import { routerPaths } from '@/router/router-path';

export interface NavMenuItem {
  name: string;
  title: string;
  icon?: string;
  route?: string;
  isCustomerAllowed?: boolean;
  children?: NavMenuItem[];
}

export type INavMenu = NavMenuItem[];

export interface PanelMenuItem {
  key: string;
  label: string;
  icon?: string;
  route?: string;
  depth?: number;
  items?: PanelMenuItem[];
}

export type PanelMenu = PanelMenuItem[];

export function item({ name, title, route, icon }: NavMenuItem): NavMenuItem {
  return {
    name,
    title,
    route,
    icon,
  };
}

export function header({ name, title, icon }: NavMenuItem): NavMenuItem {
  return {
    name,
    title,
    icon,
  };
}

export function isAllowed(item: NavMenuItem, isSeyboldUser: boolean): boolean {
  if (item.isCustomerAllowed === true) {
    return true;
  }

  return isSeyboldUser;
}

export const removeEmptyHeaders = (menu: NavMenuItem[]): NavMenuItem[] => {
  return menu
    .map((item): NavMenuItem | null => {
      if (item.children && item.children.length > 0) {
        const cleanedChildren = removeEmptyHeaders(item.children);
        if (cleanedChildren.length === 0 && !item.route) {
          return null;
        }
        return {
          ...item,
          children: cleanedChildren,
        };
      } else if (!item.route) {
        return null;
      }
      return {
        ...item,
        children: undefined,
      };
    })
    .filter((item): item is NavMenuItem => item !== null);
};

export const navigationItems: NavMenuItem[] = [
  {
    name: 'dashboard',
    title: 'navigation.dashboard',
    route: routerPaths.dashboard,
    icon: 'mdi mdi-monitor-dashboard',
    isCustomerAllowed: true,
  },
  {
    name: 'main',
    title: 'navigation.main',
    icon: 'pi pi-home',
  },
  {
    name: 'favorites',
    title: 'navigation.favorites',
    icon: 'pi pi-star-fill',
  },
  {
    name: 'company',
    title: 'navigation.company',
    icon: 'pi pi-star-fill',
  },
  {
    name: 'tickets',
    title: 'navigation.tickets',
    route: routerPaths.tickets,
    icon: 'mdi mdi-tag-check-outline',
    isCustomerAllowed: true,
  },
  {
    name: 'myTasksAndTickets',
    title: 'navigation.myTasksAndTickets',
    icon: 'mdi mdi-account-outline',
  },
  {
    name: 'classification',
    title: 'navigation.classificationOfDocumentEntries',
    route: routerPaths.classification,
    icon: 'mdi mdi-text-box-search-outline',
  },
  {
    name: 'contractsTermsAndConditions',
    title: 'navigation.contractsTermsAndConditions',
    icon: 'mdi mdi-file-sign',
  },
  {
    name: 'masterdata',
    title: 'navigation.masterdata',
    icon: 'pi pi-database',
  },
  {
    name: 'contactPersonsInternal',
    title: 'navigation.contactsInternal',
    route: routerPaths.contactpersons,
    icon: 'mdi mdi-account-voice',
  },
  {
    name: 'teams',
    title: 'navigation.teams',
    route: routerPaths.teams,
    icon: 'mdi mdi-account-group',
  },
  {
    name: 'contactPersonsCustomers',
    title: 'navigation.contactsCustomers',
    route: routerPaths.contactpersons,
    icon: 'mdi mdi-account-voice',
  },
  {
    name: 'contactPersonsPartners',
    title: 'navigation.contactsPartners',
    route: routerPaths.contactpersons,
    icon: 'mdi mdi-account-voice',
  },
  {
    name: 'companyDatabase',
    title: 'navigation.companyDatabase',
    icon: 'mdi mdi-factory',
  },
  {
    name: 'customergroups',
    title: 'navigation.customergroups',
    route: routerPaths.customergroups,
    icon: 'mdi mdi-factory',
    isCustomerAllowed: true,
  },
  {
    name: 'customers',
    title: 'navigation.customers',
    route: routerPaths.customers,
    icon: 'mdi mdi-factory',
    isCustomerAllowed: true,
  },
  {
    name: 'pointOfSales',
    title: 'navigation.pointofsales',
    route: routerPaths.pointofsales,
    icon: 'mdi mdi-point-of-sale',
    isCustomerAllowed: true,
  },
  {
    name: 'areas',
    title: 'navigation.areas',
    route: routerPaths.areas,
    icon: 'mdi mdi-map-legend',
  },
  {
    name: 'partnerCompanyDatabase',
    title: 'navigation.partnercompanyDatabase',
    icon: 'mdi mdi-handshake-outline',
  },
  {
    name: 'partnerGroups',
    title: 'navigation.partnergroups',
    route: routerPaths.partnergroups,
    icon: 'mdi mdi-handshake-outline',
  },
  {
    name: 'partners',
    title: 'navigation.partners',
    route: routerPaths.partners,
    icon: 'mdi mdi-handshake-outline',
  },
  {
    name: 'contactPersons',
    title: 'navigation.contactPerson',
    route: routerPaths.contactpersons,
    icon: 'mdi mdi-account-voice',
  },
  {
    name: 'rentalsAndContracts',
    title: 'navigation.rentalsAndContracts',
    icon: 'mdi mdi-file-document-edit-outline',
  },
  {
    name: 'contracts',
    title: 'navigation.contracts',
    route: routerPaths.contracts,
    icon: 'mdi mdi-file-document-edit-outline',
    isCustomerAllowed: true,
  },
  {
    name: 'serviceobjects',
    title: 'navigation.serviceobjects',
    route: routerPaths.serviceobjects,
  },
  {
    name: 'servicecontracts',
    title: 'navigation.servicecontracts',
    route: routerPaths.servicecontracts,
    icon: 'mdi mdi-file-document-edit-outline',
    isCustomerAllowed: true,
  },
  {
    name: 'documents',
    title: 'navigation.documents',
    route: routerPaths.documents,
    icon: 'mdi mdi-file-document-edit-outline',
  },
  {
    name: 'geography',
    title: 'navigation.geography',
    icon: 'mdi mdi-map-marker-multiple-outline',
  },
  {
    name: 'locations',
    title: 'navigation.locations',
    route: routerPaths.locations,
    icon: 'mdi mdi-map-marker-multiple-outline',
  },
  {
    name: 'address',
    title: 'navigation.address',
    route: routerPaths.address,
  },
  {
    name: 'operations',
    title: 'navigation.operations',
  },
  {
    name: 'invoiceAndContradictionData',
    title: 'navigation.invoiceAndContradictionData',
    icon: 'mdi mdi-cog-sync',
  },
  {
    name: 'facilityServices',
    title: 'navigation.facilityServices',
    icon: 'mdi mdi-office-building-cog',
  },
  {
    name: 'configuration',
    title: 'navigation.configuration',
    icon: 'pi pi-sliders-h',
  },
  {
    name: 'operatingcostinvoices',
    title: 'navigation.operatingcostinvoices',
    route: routerPaths.operatingcostinvoices,
    icon: 'mdi mdi-cash-100',
  },
  {
    name: 'costinvoiceobjections',
    title: 'navigation.costinvoiceobjections',
    route: routerPaths.costinvoiceobjections,
    icon: 'mdi mdi-abacus',
  },
  {
    name: 'sfmpositions',
    title: 'navigation.sfmpositions',
    route: routerPaths.sfmpositions,
    icon: 'mdi mdi-relation-many-to-only-one',
  },
  {
    name: 'accountDataRelated',
    title: 'navigation.accountDataRelated',
    icon: 'pi pi-user',
  },
  {
    name: 'contractDataRelated',
    title: 'navigation.contractDataRelated',
    icon: 'pi pi-file',
  },
  {
    name: 'objectDataRelated',
    title: 'navigation.objectDataRelated',
    icon: 'pi pi-box',
  },
  {
    name: 'configInvoiceAndContradictionRelatedData',
    title: 'navigation.configInvoiceAndContradictionRelatedData',
    icon: 'pi pi-box',
  },
  {
    name: 'miscellaneous',
    title: 'navigation.miscellaneous',
    icon: 'pi pi-box',
  },
  {
    name: 'myTasksAndTicketsConfiguration',
    title: 'navigation.myTasksAndTicketsConfiguration',
    icon: 'pi pi-box',
  },
  {
    name: 'administration',
    title: 'navigation.administration',
    route: routerPaths.administration,
    icon: 'pi pi-box',
    isCustomerAllowed: true,
  },
  {
    name: 'userAdministration',
    title: 'navigation.userAdministration',
    icon: 'pi pi-box',
  },
  {
    name: 'rights',
    title: 'navigation.rights',
    icon: 'pi pi-box',
  },
  {
    name: 'importFunction',
    title: 'navigation.importFunction',
    icon: 'pi pi-box',
  },
  {
    name: 'processes',
    title: 'navigation.processes',
    route: routerPaths.processes,
    icon: 'pi pi-microchip',
  },
  {
    name: 'monthlyIndex',
    title: 'navigation.monthlyindex',
    route: routerPaths.monthlyindex,
    icon: 'pi pi-key',
  },
  {
    name: 'import',
    title: 'navigation.import',
    route: routerPaths.import,
    icon: 'mdi mdi-database-import',
  },
];

export const findNavigationItem = (name: string): NavMenuItem => {
  const matchingItems = navigationItems.filter(
    (item) => item.name?.toLowerCase() === name?.toLowerCase(),
  );

  if (matchingItems.length > 1) {
    console.warn(
      `Warning: Multiple navigation items found with the name "${name}". Returning the first one.`,
    );
  } else if (!matchingItems.length) {
    console.warn(`Warning: No navigation items found with the name "${name}".`);
  }

  return (
    matchingItems[0] ||
    ({
      name,
    } as NavMenuItem)
  );
};

export const countMenuItems = (menu: NavMenuItem[]): number => {
  let count = 0;

  for (const item of menu) {
    count += 1;
    if (item.children) {
      count += countMenuItems(item.children);
    }
  }

  return count;
};

/**
 * Returns the navigation menu configuration for the given user type.
 *
 * @param isSeyboldUser - Indicates whether the current user is an internal Seybold user (`true`)
 *                        or a customer user (`false`). Internal users get the full navigation menu,
 *                        while customers get a restricted customer navigation.
 * @returns The list of navigation items appropriate for the specified user type.
 */
export const getUserSpecificNavigationMenu = (isSeyboldUser: boolean): NavMenuItem[] => {
  return isSeyboldUser ? navigationMenu : customerNavigationMenu;
};

export const navigationMenu: NavMenuItem[] = [
  {
    ...header(findNavigationItem('main')),
    children: [
      item(findNavigationItem('dashboard')),
      item(findNavigationItem('classification')),
      item(findNavigationItem('tickets')),
    ],
  },
  {
    ...header(findNavigationItem('masterdata')),
    children: [
      {
        ...header(findNavigationItem('companyDatabase')),
        children: [
          item(findNavigationItem('customerGroups')),
          item(findNavigationItem('customers')),
          // item(findNavigationItem('contactPersonsCustomers')),
        ],
      },
      {
        ...header(findNavigationItem('partnerCompanyDatabase')),
        children: [
          item(findNavigationItem('partnerGroups')),
          item(findNavigationItem('partners')),
          // item(findNavigationItem('contactPersonsPartners')),
        ],
      },
      {
        ...header(findNavigationItem('rentalsAndContracts')),
        children: [
          item(findNavigationItem('contracts')),
          item(findNavigationItem('documents')),
          item(findNavigationItem('servicecontracts')),
          item(findNavigationItem('serviceobjects')),
        ],
      },
      {
        ...header(findNavigationItem('geography')),
        children: [
          item(findNavigationItem('pointOfSales')),
          item(findNavigationItem('areas')),
          item(findNavigationItem('locations')),
        ],
      },
    ],
  },
  {
    ...header(findNavigationItem('operations')),
    children: [
      item(findNavigationItem('processes')),
      item(findNavigationItem('monthlyIndex')),
      item(findNavigationItem('import')),
    ],
  },
  {
    ...header(findNavigationItem('company')),
    children: [
      item(findNavigationItem('contactPersonsInternal')),
      item(findNavigationItem('teams')),
      item(findNavigationItem('operatingcostinvoices')),
      item(findNavigationItem('costinvoiceobjections')),
      item(findNavigationItem('sfmpositions')),
    ],
  },
];

export const customerNavigationMenu: NavMenuItem[] = [
  {
    ...header(findNavigationItem('main')),
    children: [
      item(findNavigationItem('dashboard')),
      item(findNavigationItem('customers')),
      item(findNavigationItem('pointOfSales')),
      item(findNavigationItem('contracts')),
      item(findNavigationItem('servicecontracts')),
      item(findNavigationItem('tickets')),
    ],
  },
];

export function filterNavigationMenu(isSeyboldUser: boolean): NavMenuItem[] {
  function filterItems(items?: NavMenuItem[]): NavMenuItem[] {
    if (!items) return [];

    return items
      .map((item): NavMenuItem | null => {
        try {
          const navigationItem = findNavigationItem(item.name);

          if (!navigationItem?.name) {
            console.warn(`[navigation] entry "${item.name}" not found.`);
            return null;
          }

          // If not allowed AND this node is an actual route, remove it
          if (!isAllowed(navigationItem, isSeyboldUser) && !!navigationItem.route) {
            return null;
          }

          const filteredChildren = filterItems(item.children);

          if (filteredChildren.length > 0) {
            return { ...item, children: filteredChildren };
          }

          // no child: keep only if it's a route
          if (!item.route) return null;

          return { ...item };
        } catch (error) {
          console.error(`[Navigation] Error on adding of item "${item.name}":`, error);
          return null;
        }
      })
      .filter((x): x is NavMenuItem => x !== null);
  }

  return filterItems(getUserSpecificNavigationMenu(isSeyboldUser));
}
